// DropSpacer.tsx
import { Box, useTheme } from '@mui/material';
import AppVal from 'constants/frontend/app/AppVal';
import { useDrop } from 'react-dnd';

import { useEffect, useState } from 'react';
import { ANIMATION_DURATION_MS } from './LessonReportTemplateAddContainer';

type DragItem = { index: number; height: number };

function DropSpacer({
  index,
  onDropAt,
}: {
  index: number; // 0..length
  onDropAt: (fromIndex: number, rawToIndex: number, animationDurationMs: number) => void;
}) {
  const { palette } = useTheme();
  const [justDropped, setJustDropped] = useState(false);

  const [{ isOver, canDrop, dragHeight, isDragging }, drop] = useDrop<
    DragItem,
    void,
    { isOver: boolean; canDrop: boolean; dragHeight: number | null; isDragging: boolean }
  >({
    accept: AppVal.DRAG_ITEM_EXISTING_HOMEWORK,
    drop: (dragItem) => {
      onDropAt(dragItem.index, index, ANIMATION_DURATION_MS);
      setJustDropped(true); // ✅ 이 spacer에 실제로 drop 됨
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      dragHeight: monitor.getItem()?.height ?? null,
      isDragging: !!monitor.getItem(),
    }),
  });

  // 드래그가 완전히 끝나면 다음 드래그를 위해 초기화
  useEffect(() => {
    if (!isDragging && justDropped) {
      setJustDropped(false);
    }
  }, [isDragging, justDropped]);

  const height =
    isOver && canDrop
      ? dragHeight
        ? Math.max(16, dragHeight + 16)
        : 16 // 최소 16, 없으면 16
      : 8;

  // drop 후 height→0으로 갈 때만 transition 끔
  const shouldAnimateHeight = !(justDropped && !isOver);

  return (
    <Box
      ref={drop}
      sx={{
        py: isOver ? 1 : 0,
        position: 'relative',
        height,
        transition: shouldAnimateHeight ? `height ${ANIMATION_DURATION_MS}ms ease` : 'none',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          bottom: 8,
          left: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            // 캡슐 모양의 dashed border 박스
            'height': '100%',
            'width': '100%',
            'borderRadius': '8px',
            'boxSizing': 'border-box',
            // 기본 상태 (아무 것도 안 떨어져 있을 때)
            'backgroundColor': 'transparent',

            // isOver && canDrop 일 때만 skeleton 스타일 + 깜빡임
            ...(isOver &&
              canDrop && {
                backgroundColor: palette.background.paper,
                opacity: 0.25,
                animation: 'blinkSkeleton 1s ease-in-out infinite',
              }),

            // keyframes 정의
            '@keyframes blinkSkeleton': {
              '0%': { opacity: 0.15 },
              '50%': { opacity: 0.45 },
              '100%': { opacity: 0.15 },
            },
          }}
        />
      </Box>
    </Box>
  );
}

export default DropSpacer;
